import type { ProductFacetModel } from "@/api";

/** Matches KriyaDevDB `ProductFacet.Description` nvarchar(2000). */
export const PRODUCT_FACET_DESCRIPTION_MAX_LENGTH = 2000;

export function truncateProductFacetDescription(
  value: string | null | undefined,
): string | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  if (trimmed.length <= PRODUCT_FACET_DESCRIPTION_MAX_LENGTH) {
    return trimmed;
  }
  return trimmed.slice(0, PRODUCT_FACET_DESCRIPTION_MAX_LENGTH);
}

/** Org profile parent name for facet type lookup. */
export const PRODUCT_FACET_TYPE_PARENT_NAME = "Product Facet Type";

/** Org profile parent name for facet status lookup. */
export const PRODUCT_FACET_STATUS_PARENT_NAME = "Product Facet Status";

/** Org profile parent for facet ↔ work relationship types (MVP: implements). */
export const PRODUCT_FACET_WORK_RELATIONSHIP_PARENT_NAME =
  "Product Facet Work Relationship";

export const PRODUCT_FACET_WORK_RELATIONSHIP_IMPLEMENTS = "implements";

/** Canonical facet type keys (match org profile names). Role is deprecated — not used in hierarchy. */
export const PRODUCT_FACET_TYPE_KEYS = [
  "Product",
  "Function",
  "JTBD",
  "Experience",
  "Capability",
  "Feature",
] as const;

/** @deprecated Legacy facet type — may exist in org profiles; skipped in grid. */
export const LEGACY_PRODUCT_FACET_TYPE_ROLE = "Role" as const;

export type ProductFacetTypeKey = (typeof PRODUCT_FACET_TYPE_KEYS)[number];

/** Grid columns — excludes Product root; starts at Product Domain (Function). */
export const PRODUCT_GRID_COLUMN_KEYS = [
  "Function",
  "JTBD",
  "Experience",
  "Capability",
  "Feature",
] as const;

export type ProductGridColumnKey = (typeof PRODUCT_GRID_COLUMN_KEYS)[number];

export const PRODUCT_GRID_COLUMN_LABELS: Record<ProductGridColumnKey, string> =
  {
    Function: "Domain",
    JTBD: "Goal",
    Experience: "User Flow",
    Capability: "Capability",
    Feature: "Feature",
  };

/** Default columns shown on first visit (before localStorage preference). */
export const DEFAULT_PRODUCT_GRID_VISIBLE_COLUMNS = [
  ...PRODUCT_GRID_COLUMN_KEYS,
] as const satisfies readonly ProductGridColumnKey[];

/** Preserve canonical grid column order when applying visibility. */
export function resolveVisibleProductGridColumns(
  columns: readonly ProductGridColumnKey[] | undefined,
  fallback: readonly ProductGridColumnKey[] = DEFAULT_PRODUCT_GRID_VISIBLE_COLUMNS,
): ProductGridColumnKey[] {
  const source = columns && columns.length > 0 ? columns : fallback;
  const visibleSet = new Set(source);
  const ordered = PRODUCT_GRID_COLUMN_KEYS.filter((column) =>
    visibleSet.has(column),
  );

  return ordered.length > 0 ? [...ordered] : [...fallback];
}

/** Child type allowed under each parent type. */
export const PRODUCT_FACET_CHILD_TYPE: Record<
  ProductFacetTypeKey,
  ProductFacetTypeKey | null
> = {
  Product: "Function",
  Function: "JTBD",
  JTBD: "Experience",
  Experience: "Capability",
  Capability: "Feature",
  Feature: null,
};

export type ProductFacetTypeLookup = Record<
  ProductFacetTypeKey,
  string | undefined
>;

export interface ProductFacetTreeNode extends ProductFacetModel {
  productFacetTypeName?: ProductFacetTypeKey | null;
  statusName?: string | null;
  /** Unpersisted AI draft node shown during M3 review. */
  isAiDraft?: boolean;
  subRows?: ProductFacetTreeNode[];
}

export interface ProductGridCell {
  facetId: string;
  name: string;
  typeKey: ProductGridColumnKey;
  statusName?: string | null;
  /** JTBD metadata — who performs this goal. */
  roles?: string[];
}

export interface ProductGridRow {
  rowKey: string;
  cells: Partial<Record<ProductGridColumnKey, ProductGridCell>>;
  isComplete: boolean;
  deepestType: ProductGridColumnKey;
  parentFacetId: string | null;
}

export interface ProductGraphNode {
  id: string;
  parentId: string | null;
  productId: string | null;
  name: string;
  description?: string | null;
  typeKey: ProductFacetTypeKey;
  statusName?: string | null;
  statusId?: string | null;
  isAiDraft?: boolean;
}

export type ProductPageViewId =
  | "grid"
  | "graph"
  | "projects"
  | "intelligence"
  | "repositories";

export const PRODUCT_PAGE_VIEW_IDS = [
  "intelligence",
  "grid",
  "graph",
  "projects",
  "repositories",
] as const;

export interface ProductDraft {
  name: string;
  description?: string;
}

export const PRODUCT_DRAFT_SESSION_KEY = "productOnboardingDraft";

/** Sidebar project filter — persisted per org in localStorage. */
export type ProductFilter = "all" | { productId: string };

export function getProductIdFromFilter(
  filter: ProductFilter,
): string | undefined {
  return typeof filter === "object" ? filter.productId : undefined;
}

/** True when a project root has no Function facet link (Work.productId). */
export function isWorkUnassignedToProduct(work: {
  productId?: string | null;
}): boolean {
  const productId = work.productId;
  return productId == null || productId.trim() === "";
}

export function getProductFilterLabel(
  filter: ProductFilter,
  products: ReadonlyArray<{ id?: string; name?: string | null }>,
): string {
  if (filter === "all") return "Products";
  const product = products.find((p) => p.id === filter.productId);
  return product?.name?.trim() || "Product";
}

export function productFilterStorageKey(orgId: string): string {
  return `kriya-product-filter-${orgId}`;
}

/** JTBD facet metadata — roles who perform this job (stored on facet jsonData). */
export interface JtbdRolesMetadata {
  roles: string[];
}

export function parseJtbdRolesMetadata(
  jsonData: string | null | undefined,
): JtbdRolesMetadata | null {
  if (!jsonData?.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(jsonData);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    const raw = record.jtbdRoles;
    if (!Array.isArray(raw)) return null;
    const roles = raw
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    return { roles };
  } catch {
    return null;
  }
}

export function mergeJtbdRolesJson(
  existingJsonData: string | null | undefined,
  roles: readonly string[],
): string {
  let base: Record<string, unknown> = {};
  if (existingJsonData?.trim()) {
    try {
      const parsed: unknown = JSON.parse(existingJsonData);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        base = { ...(parsed as Record<string, unknown>) };
      }
    } catch {
      base = {};
    }
  }

  const normalized = [...new Set(roles.map((role) => role.trim()).filter(Boolean))];

  return JSON.stringify({
    ...base,
    jtbdRoles: normalized,
  });
}

export function formatJtbdRolesTooltip(roles: readonly string[]): string | null {
  if (roles.length === 0) return null;
  if (roles.length === 1) {
    return `Role: ${roles[0]}`;
  }
  return `Roles: ${roles.join(", ")}`;
}

/** Grid hover text for JTBD cells — always returns a hint so roles are discoverable. */
export function formatJtbdRolesGridTooltip(roles: readonly string[]): string {
  return formatJtbdRolesTooltip(roles) ?? "No roles yet — add roles in the sidebar";
}
